import React, { useState, useMemo } from "react";
import {
  Form,
  Button,
  Badge,
  Spinner,
  Table,
  Alert,
  Modal,
} from "react-bootstrap";
import { FaPlus, FaShieldAlt, FaTrash, FaKey, FaCopy, FaCheck, FaEdit } from "react-icons/fa";
import { useQueryClient } from "@tanstack/react-query";
import { useGetPermissions } from "../../hooks/useGetPermissions";
import { useCreateApiKey } from "../../hooks/useCreateApiKey";
import { useGetApiKeys } from "../../hooks/useGetApiKeys";
import { useUpdateApiKey } from "../../hooks/useUpdateApiKey";
import { useDeleteApiKey } from "../../hooks/useDeleteApiKey";

// ─── Permissions matrix ──────────────────────────────────────────────────────

const PermissionsMatrix = ({ permissionsByCategory, selectedIds, onToggle, onToggleCategory }) => (
  <div className="border rounded p-0">
    <div className="table-responsive" style={{ maxHeight: "340px", overflowY: "auto" }}>
      <Table size="sm" className="mb-0">
        <thead className="bg-body-secondary sticky-top">
          <tr>
            <th className="fw-bold py-2 px-3">Permission</th>
            <th className="fw-bold py-2 px-3 text-center" style={{ width: "80px" }}>
              Activée
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(permissionsByCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, perms]) => {
              const catIds = perms.map((p) => p.id);
              const allChecked = catIds.every((id) => selectedIds.includes(id));
              const someChecked = catIds.some((id) => selectedIds.includes(id));
              return (
                <React.Fragment key={category}>
                  <tr className="table-active">
                    <td className="fw-bold py-2 px-3 small">📋 {category}</td>
                    <td className="py-2 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked && !allChecked;
                        }}
                        onChange={() => onToggleCategory(perms)}
                        title="Tout sélectionner / désélectionner"
                        style={{ cursor: "pointer", transform: "scale(1.2)" }}
                      />
                    </td>
                  </tr>
                  {perms.map((perm) => {
                    const isChecked = selectedIds.includes(perm.id);
                    return (
                      <tr key={perm.id} className={isChecked ? "bg-primary bg-opacity-10" : ""}>
                        <td className="py-2 px-3 ps-4">
                          <code className="text-body">{perm.nom}</code>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <Form.Check
                            type="checkbox"
                            id={`perm-${perm.id}`}
                            checked={isChecked}
                            onChange={() => onToggle(perm.id)}
                            className="d-inline-block"
                            style={{ cursor: "pointer", transform: "scale(1.2)" }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
        </tbody>
      </Table>
    </div>
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

const normalizeApiKeys = (payload) => {
  const list =
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.keys) && payload.keys) ||
    (Array.isArray(payload) && payload) ||
    [];

  return list.map((k, index) => ({
    id: k?.id ?? `server-${index}`,
    module: k?.module_name ?? k?.module ?? "—",
    expiryDate: k?.expiration_date ?? k?.expiration ?? "",
    isActive: k?.is_active ?? true,
    permissions: Array.isArray(k?.permissions) ? k.permissions : [],
    createdAt: k?.created_at
      ? new Date(k.created_at).toLocaleDateString("fr-FR")
      : "—",
  }));
};

const API = ({ titre }) => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState("form");
  const [formData, setFormData] = useState({ module: "", expiryDate: "", permissions: [] });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editExpiry, setEditExpiry] = useState("");

  const createApiKey = useCreateApiKey();
  const updateApiKey = useUpdateApiKey();
  const deleteApiKey = useDeleteApiKey();
  const { data: apiKeysResponse, isLoading: apiKeysLoading } = useGetApiKeys();
  const apiKeys = useMemo(() => normalizeApiKeys(apiKeysResponse), [apiKeysResponse]);

  const { data: permissionsData, isLoading: permissionsLoading, isError: permissionsError } =
    useGetPermissions();

  const allPermissions = useMemo(() => {
    const perms = permissionsData?.data || [];
    return perms.map((p) => ({
      id: p.id,
      nom: p.name,
      categorie: p.name.includes("_") ? p.name.split("_")[1] : "Autres",
    }));
  }, [permissionsData]);

  const permissionsByCategory = useMemo(
    () =>
      allPermissions.reduce((acc, perm) => {
        const cat = perm.categorie || "Autres";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(perm);
        return acc;
      }, {}),
    [allPermissions]
  );

  const openModal = () => {
    setFormData({ module: "", expiryDate: "", permissions: [] });
    setGeneratedKey(null);
    setCopied(false);
    setStep("form");
    setShowModal(true);
  };

  const togglePermission = (id) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(id)
        ? prev.permissions.filter((p) => p !== id)
        : [...prev.permissions, id],
    }));
  };

  const toggleCategory = (perms) => {
    const catIds = perms.map((p) => p.id);
    const allChecked = catIds.every((id) => formData.permissions.includes(id));
    setFormData((prev) => ({
      ...prev,
      permissions: allChecked
        ? prev.permissions.filter((id) => !catIds.includes(id))
        : [...new Set([...prev.permissions, ...catIds])],
    }));
  };

  const formatExpiration = (datetimeLocal) => {
    if (!datetimeLocal) return "";
    return datetimeLocal.replace("T", " ") + ":00";
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    createApiKey.mutate(
      {
        module: formData.module,
        permissions: formData.permissions,
        expiration: formatExpiration(formData.expiryDate),
      },
      {
        onSuccess: (key) => {
          setGeneratedKey(key ?? "");
          queryClient.invalidateQueries({ queryKey: ["api-keys"] });
          setStep("success");
        },
      }
    );
  };

  const handleCopy = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevoke = () => {
    deleteApiKey.mutate(revokeTarget, {
      onSuccess: () => setRevokeTarget(null),
      onError: () => setRevokeTarget(null),
    });
  };

  const openEditModal = (k) => {
    let iso = "";
    if (k.expiryDate) {
      const d = new Date(k.expiryDate);
      if (!isNaN(d)) iso = d.toISOString().slice(0, 16);
    }
    setEditTarget(k);
    setEditExpiry(iso);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    updateApiKey.mutate(
      { id: editTarget.id, data: { expiration: formatExpiration(editExpiry) } },
      { onSuccess: () => setEditTarget(null) }
    );
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <>
      {titre && (
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800 font-weight-bold">{titre}</h1>
        </div>
      )}

      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0 d-flex align-items-center gap-2">
          <FaKey /> Clés API
        </h5>
        <Button variant="primary" size="sm" onClick={openModal}>
          <FaPlus className="me-2" />
          Nouvelle clé
        </Button>
      </div>

      {/* ── Keys list ── */}
      <div className="card shadow mb-4">
        <div className="card-body p-0">
          {apiKeysLoading ? (
            <div className="text-center text-muted py-5">
              <Spinner animation="border" size="sm" />
              <div className="mt-2">Chargement des clés API…</div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center text-muted py-5">
              <FaKey size={32} className="mb-3 opacity-25" />
              <p className="mb-1">Aucune clé API générée</p>
              <small>Cliquez sur "Nouvelle clé" pour en créer une.</small>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead className="bg-body-secondary">
                  <tr>
                    <th className="py-3 px-3">Module</th>
                    <th className="py-3 px-3">Créée le</th>
                    <th className="py-3 px-3">Expiration</th>
                    <th className="py-3 px-3">Permissions</th>
                    <th className="py-3 px-3">Statut</th>
                    <th className="py-3 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => {
                    const expired = isExpired(k.expiryDate);
                    return (
                      <tr key={k.id}>
                        <td className="py-3 px-3 fw-semibold">{k.module}</td>
                        <td className="py-3 px-3 text-muted small">{k.createdAt}</td>
                        <td className="py-3 px-3 small">
                          {k.expiryDate
                            ? new Date(k.expiryDate).toLocaleDateString("fr-FR")
                            : "—"}
                        </td>
                        <td className="py-3 px-3">
                          <Badge bg="secondary">
                            {k.permissions.length} permission{k.permissions.length !== 1 ? "s" : ""}
                          </Badge>
                        </td>
                        <td className="py-3 px-3">
                          <Badge bg={!k.isActive ? "dark" : expired ? "danger" : "success"}>
                            {!k.isActive ? "Révoquée" : expired ? "Expirée" : "Active"}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-end">
                          {k.isActive && (
                            <>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-2"
                                onClick={() => openEditModal(k)}
                                title="Modifier l'expiration"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => setRevokeTarget(k.id)}
                              >
                                <FaTrash />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">
            {step === "form" ? "Nouvelle clé API" : "Clé générée avec succès"}
          </Modal.Title>
        </Modal.Header>

        {step === "form" ? (
          <Form onSubmit={handleGenerate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Module</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Application mobile, Webhook prod…"
                  value={formData.module}
                  onChange={(e) => setFormData((prev) => ({ ...prev, module: e.target.value }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Date d'expiration</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-1">
                <Form.Label className="fw-semibold d-flex align-items-center gap-2 mb-2">
                  <FaShieldAlt size={13} /> Permissions
                </Form.Label>

                {permissionsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2 text-muted">Chargement…</span>
                  </div>
                ) : permissionsError ? (
                  <Alert variant="danger">Erreur lors du chargement des permissions</Alert>
                ) : (
                  <PermissionsMatrix
                    permissionsByCategory={permissionsByCategory}
                    selectedIds={formData.permissions}
                    onToggle={togglePermission}
                    onToggleCategory={toggleCategory}
                  />
                )}
                <small className="text-muted mt-1 d-block">
                  Cochez une catégorie pour tout sélectionner / désélectionner.
                </small>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  createApiKey.isPending ||
                  !formData.module ||
                  !formData.expiryDate ||
                  formData.permissions.length === 0
                }
              >
                {createApiKey.isPending ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Génération…
                  </>
                ) : (
                  <>
                    <FaPlus className="me-2" />
                    Générer
                  </>
                )}
              </Button>
              {createApiKey.isError && (
                <Alert variant="danger" className="mt-2 mb-0 py-2">
                  <small>{createApiKey.error?.message ?? "Erreur lors de la génération"}</small>
                </Alert>
              )}
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body>
              <div className="text-center mb-4">
                <div
                  className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: 56, height: 56 }}
                >
                  <FaCheck className="text-success" size={22} />
                </div>
                <h6 className="fw-bold mb-1">Clé générée</h6>
                <p className="text-muted small mb-0">
                  Copiez cette clé maintenant — vous ne pourrez plus la voir.
                </p>
              </div>

              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  value={generatedKey}
                  readOnly
                  className="font-monospace"
                  style={{
                    backgroundColor: "var(--bs-body-bg)",
                    color: "var(--bs-body-color)",
                    borderColor: "var(--bs-border-color)",
                  }}
                />
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  className="text-body"
                  onClick={handleCopy}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </Button>
              </div>

              <Alert variant="danger" className="py-2 mb-0">
                <small>
                  ⚠️ <strong>Important :</strong> Cette clé ne sera plus visible après fermeture.
                  Conservez-la en lieu sûr.
                </small>
              </Alert>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => setShowModal(false)}>
                Fermer
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* ── Edit expiration modal ── */}
      <Modal show={!!editTarget} onHide={() => setEditTarget(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">Modifier l'expiration</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdate}>
          <Modal.Body>
            <Form.Group>
              <Form.Label className="fw-semibold">Nouvelle date d'expiration</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
                required
              />
            </Form.Group>
            {updateApiKey.isError && (
              <Alert variant="danger" className="mt-3 mb-0 py-2">
                <small>{updateApiKey.error?.message ?? "Erreur lors de la mise à jour"}</small>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setEditTarget(null)}>
              Annuler
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={updateApiKey.isPending || !editExpiry}
            >
              {updateApiKey.isPending ? <Spinner size="sm" animation="border" /> : "Enregistrer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Revoke confirm ── */}
      <Modal show={!!revokeTarget} onHide={() => setRevokeTarget(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold">Révoquer la clé</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted small">
          Cette action est irréversible. La clé sera immédiatement désactivée.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setRevokeTarget(null)}>
            Annuler
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleRevoke}
            disabled={deleteApiKey.isPending}
          >
            {deleteApiKey.isPending ? <Spinner size="sm" animation="border" /> : "Révoquer"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default API;
